import { useMemo } from 'react';
import { useQueries, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { api } from '../utils/api'
import { ApiResponse, ApiError } from '../types/api'

import { Strategy } from '../types/strategy';

export const useStrategies = (projectId: number) => {
    const { data: strategies, isLoading, error } = useQuery({
        queryKey: [projectId, 'strategies'],
        queryFn:  () => api.request<ApiResponse<Strategy[]>>(`/api/projects/${projectId}/mitigationStrategies`),
        refetchOnWindowFocus: false
    }) as { data: ApiResponse<Strategy[]>, isLoading: boolean, error: ApiError };

    return { strategies, isLoading, error };
};

/** Strategies for several projects at once (same cache entries as useStrategies). */
export const useStrategiesForProjects = (
    projectIds: (number | string)[]
): { byProject: Record<string, Strategy[]>; isPending: boolean } => {
    const ids = useMemo(() => projectIds.map((id) => Number(id)), [projectIds]);
    return useQueries({
        queries: ids.map((projectId) => ({
            queryKey: [projectId, 'strategies'],
            queryFn: () => api.request<ApiResponse<Strategy[]>>(`/api/projects/${projectId}/mitigationStrategies`),
            refetchOnWindowFocus: false as const,
        })),
        combine: (results) => {
            const byProject: Record<string, Strategy[]> = {};
            results.forEach((q, i) => {
                if (q.data?.data) byProject[String(ids[i])] = q.data.data;
            });
            return { byProject, isPending: results.some((q) => q.isPending) };
        },
    });
};

export const useAddStrategy = (projectId: number) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<any>, void, string>({
        mutationFn: (strategyName) => {
            return api.request(`/api/projects/${projectId}/mitigationStrategies`, {
                method: "POST",
                body: {
                    name: strategyName,
                    description: ""
                }
            });
        },    
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [projectId, 'strategies']});
        },
    })
};

export const useUpdateStrategy = (projectId: number) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Strategy>, ApiError, { strategyId: string, name: string, description: string }>({        
        mutationFn: async ({ strategyId, name, description }) => {
            const response: ApiResponse<Strategy> = await api.request(`/api/projects/${projectId}/mitigationStrategies/${strategyId}/`, {
                method: 'PUT',
                body: { name, description }
            });
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [projectId, 'strategies'] });
        },
    });
};

export const useCloneStrategy = (projectId: number) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Strategy>, ApiError, string>({        
        mutationFn: async (strategyId) => {
            const response: ApiResponse<Strategy> = await api.request(`/api/projects/${projectId}/mitigationStrategies/${strategyId}/`, {
                method: 'POST'
            });
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [projectId, 'strategies'] });
        },
    });
};

export const useDeleteStrategy = (projectId: number) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<any>, ApiError, string>({
        mutationFn: async (strategyId) => {
            const response: ApiResponse<any> = await api.request(`/api/projects/${projectId}/mitigationStrategies/${strategyId}/`, {
                method: 'DELETE',
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [projectId, 'strategies'] });
        },
    });
};
